import React from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const SchoolDashboardPreview: React.FC = () => {
  const classes = [
    { name: 'Year 5A', teacher: 'Ms. Johnson', students: 28, mastery: 82, trend: 'up', change: 4 },
    { name: 'Year 5B', teacher: 'Mr. Patel', students: 26, mastery: 75, trend: 'up', change: 2 },
    { name: 'Year 5C', teacher: 'Mrs. Chen', students: 27, mastery: 68, trend: 'down', change: -3 },
    { name: 'Year 6A', teacher: 'Mr. Williams', students: 29, mastery: 85, trend: 'up', change: 6 },
    { name: 'Year 6B', teacher: 'Ms. Brown', students: 25, mastery: 79, trend: 'up', change: 1 },
  ];

  const getMasteryColor = (mastery: number) => {
    if (mastery >= 80) return 'var(--color-emerald)';
    if (mastery >= 70) return 'var(--color-electric-blue)';
    if (mastery >= 60) return 'var(--color-amber)';
    return 'var(--color-rose)';
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="card-premium overflow-hidden w-full max-w-lg"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-[var(--color-electric-blue)]" />
            <span className="text-white font-semibold">Class Performance Overview</span>
          </div>
          <span className="text-xs text-[var(--color-medium-gray)]">Mathematics - Term 3</span>
        </div>
      </div>

      {/* Class List */}
      <div className="p-4">
        <div className="space-y-3">
          {classes.map((classItem, index) => (
            <motion.div
              key={classItem.name}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-white font-semibold">{classItem.name}</span>
                  <span className="text-[var(--color-medium-gray)] text-sm ml-2">• {classItem.teacher}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <UsersIcon className="w-3 h-3 text-[var(--color-medium-gray)]" />
                    <span className="text-xs text-[var(--color-medium-gray)]">{classItem.students}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {classItem.trend === 'up' ? (
                      <ArrowTrendingUpIcon className="w-4 h-4 text-[var(--color-emerald)]" />
                    ) : (
                      <ArrowTrendingDownIcon className="w-4 h-4 text-[var(--color-rose)]" />
                    )}
                    <span
                      className={`text-xs font-semibold ${
                        classItem.trend === 'up' ? 'text-[var(--color-emerald)]' : 'text-[var(--color-rose)]'
                      }`}
                    >
                      {classItem.change > 0 ? '+' : ''}{classItem.change}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-white/10 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${classItem.mastery}%` }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="h-2 rounded-full"
                    style={{ backgroundColor: getMasteryColor(classItem.mastery) }}
                  />
                </div>
                <span
                  className="text-sm font-bold min-w-[40px] text-right"
                  style={{ color: getMasteryColor(classItem.mastery) }}
                >
                  {classItem.mastery}%
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Row */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-light-gray)]">School Average</span>
            <div className="flex items-center gap-3">
              <div className="w-24 bg-white/10 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-[var(--color-electric-blue)] to-[var(--color-emerald)]"
                  style={{ width: '78%' }}
                />
              </div>
              <span className="text-[var(--color-electric-blue)] font-bold">78%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-white/10 bg-white/5">
        <div className="flex items-center justify-between">
          <button className="text-sm text-[var(--color-electric-blue)] hover:text-white transition-colors">
            View Detailed Report
          </button>
          <button className="text-sm text-[var(--color-electric-blue)] hover:text-white transition-colors">
            Export Data
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SchoolDashboardPreview;
